/**
 * @fileoverview Shared local-first violation picker component used across
 *               system-web (template builder, system code library row
 *               actions) and tenant-web (template builder field mapping,
 *               violations module). Reads `violationCodeSets` and
 *               `violationIndex` directly from a passed-in Dexie handle —
 *               never makes API calls and never falls back to mock data.
 * @module local-db/components/ViolationPicker
 *
 * Responsibilities:
 * - Provide a single, reusable UI for picking one (or many) violations from
 *   the synced local code library.
 * - Support optional code-set scoping, multi-select, and a pluggable ranker
 *   so Phase 5 AI smart-search can swap in semantic ranking without
 *   touching the picker UI.
 *
 * Dependencies:
 * - react, lucide-react (icon set used elsewhere in the package).
 * - dexie (via `liveQuery` in `violationPickerService`).
 *
 * Architecture:
 * - Strict local-first: every read is a Dexie liveQuery subscription. The
 *   component never imports apiClient or fetches.
 * - The injected `db` handle is the per-app Dexie instance (system-web uses
 *   `getSystemDexieRaw()`, tenant-web uses `getDexieRaw()`).
 *
 * Notes:
 * - The picker is presentation-only: callers own selection state. This keeps
 *   it composable in both controlled (template builder field mapping) and
 *   transient (one-shot pick) flows.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronLeft, ChevronRight, Search, BookOpen, X, Check, Sparkles, Loader2, ListTree } from 'lucide-react';
import {
  rankViolationEntries,
  watchActiveViolationCodeSets,
  watchViolationCountsByCodeSet,
  watchViolationIndex,
  watchViolationChapters,
  type ViolationAsyncRankFn,
  type ViolationCodeSetEntry,
  type ViolationIndexEntry,
  type ViolationChapterEntry,
  type ViolationChapterSectionEntry,
  type ViolationRankFn,
} from '../services/violationPickerService';
import {
  watchActiveBackgroundJobsByType,
  type BackgroundJob,
} from '../services/backgroundJobService';

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * @contract ViolationPickerSelection
 *
 * Purpose:
 * - Minimal payload returned for each violation pick. Larger consumers can
 *   look the rest up by `_id` from Dexie.
 *
 * Fields:
 * - violationId: stable Dexie `_id`.
 * - codeSetId:   parent code-set id.
 * - code:        section code (e.g. "1006.2").
 * - title:       short title.
 * - codeReference/contentSnippet/displayText/bodyText: optional human-readable
 *   detail fields for caller-side summaries/modals.
 *
 * Sync:
 * - N/A; UI-event payload only.
 */
export interface ViolationPickerSelection {
  violationId: string;
  codeSetId: string;
  code: string;
  title: string;
  codeReference?: string;
  contentSnippet?: string;
  displayText?: string;
  bodyText?: string;
}

/**
 * @contract ViolationPickerProps
 *
 * Purpose:
 * - Public prop surface for the shared component.
 *
 * Fields:
 * - db:           Dexie handle for the host app.
 * - mode:         'single' (commit on click) or 'multi' (toggleable selection).
 * - selectedIds:  When mode='multi', controlled list of chosen violation ids.
 * - onChange:     When mode='multi', emits the next selection list.
 * - onPickOne:    When mode='single', emits each click.
 * - codeSetIds:   Optional restriction to a subset of code sets.
 * - rankFn:       Optional ranker injection (Phase 5 AI semantic ranker).
 * - placeholder:  Search input placeholder.
 * - autoFocus:    Focus the search input on mount.
 * - emptyHint:    Custom text shown when no entries exist.
 * - className:    Optional outer class.
 *
 * Rules:
 * - Either `mode='single'` + `onPickOne`, OR `mode='multi'` + `selectedIds`
 *   + `onChange` must be provided. Mixed is undefined.
 *
 * Sync:
 * - Reads only; no writes.
 */
export interface ViolationPickerProps {
  db: any;
  mode?: 'single' | 'multi';
  selectedIds?: string[];
  onChange?: (next: ViolationPickerSelection[]) => void;
  onPickOne?: (selection: ViolationPickerSelection) => void;
  codeSetIds?: string[];
  /**
   * Optional sync ranker. Defaults to lexical `rankViolationEntries`.
   * Used as the always-on baseline and the offline/error fallback for
   * `asyncRankFn`.
   */
  rankFn?: ViolationRankFn;
  /**
   * Optional async ranker (Phase 5 AI smart-search). When provided, the
   * picker shows an "AI smart search" toggle. While the toggle is on, each
   * (debounced) query is sent to the async ranker and its result replaces
   * the lexical list. The toggle state persists per-user via the
   * `aiToggleStorageKey` localStorage key.
   */
  asyncRankFn?: ViolationAsyncRankFn;
  /**
   * Default state for the AI toggle when `asyncRankFn` is provided. Defaults
   * to true (AI on), since the host opted in by passing the prop.
   */
  aiSearchDefaultEnabled?: boolean;
  /** localStorage key for persisting the AI toggle. */
  aiToggleStorageKey?: string;
  placeholder?: string;
  autoFocus?: boolean;
  emptyHint?: string;
  className?: string;
}

interface BrowseNode {
  key: string;
  label: string;
  violation?: ViolationIndexEntry;
  children: BrowseNode[];
  count: number;
}

function normalizeDisplayText(value: string | undefined): string {
  return (value ?? '').replace(/\s+/g, ' ').trim();
}

function isRawSourceIdentifier(value: string | undefined): boolean {
  return Boolean(value && /^CA[A-Z]+20\d{2}P\d_/i.test(value));
}

function titleCaseSourceIdentifier(value: string): string {
  return value
    .replace(/^CA[A-Z]+20\d{2}P\d_/i, '')
    .replace(/^Pt\d+_/i, '')
    .replace(/_/g, ' ')
    .replace(/\btitlepage\b/i, 'Title Page')
    .replace(/\bcopyrightpage\b/i, 'Copyright Page')
    .replace(/\bPref\b/i, 'Preface')
    .replace(/\bAckn\b/i, 'Acknowledgments')
    .replace(/\bCALICODEs\b/i, 'California Code of Regulations, Title 24')
    .replace(/\bHOWTO\b/i, 'How to Distinguish Between Model Code Language and California Amendments')
    .replace(/\bCaliMat\b/i, 'California Matrix Adoption Tables')
    .replace(/\bMM\b/i, 'Marginal Markings')
    .replace(/\bCIC\b/i, 'California Interagency Committee')
    .replace(/\bAAF\b/i, 'Adoption of Administrative Regulations')
    .replace(/\bIndex\b/i, 'Index')
    .replace(/\bHISTORYNOTE\b/i, 'History Note Appendix')
    .replace(/\bCh(\d+)\b/i, 'Chapter $1')
    .replace(/\bAppx([A-Z0-9]+)\b/i, 'Appendix $1')
    .replace(/\s+/g, ' ')
    .trim();
}

function humanizeLabel(value: string | undefined): string {
  const text = normalizeDisplayText(value);
  if (!text) return '';
  return isRawSourceIdentifier(text) ? titleCaseSourceIdentifier(text) : text;
}

function joinCodeAndTitle(code: string, title: string): string {
  if (!code) return title;
  if (!title || title.toLowerCase() === code.toLowerCase()) return code;
  return `${code} ${title}`;
}

function chapterLabel(chapter: ViolationChapterEntry): string {
  const ordinal = humanizeLabel(chapter.ordinal);
  const code = ordinal && /^\d+$/.test(ordinal) ? `Chapter ${ordinal}` : ordinal;
  const title = humanizeLabel(chapter.title || chapter.chapterId);
  return joinCodeAndTitle(code, title) || title || 'Untitled chapter';
}

function sectionNodeId(section: ViolationChapterSectionEntry, index: number): string {
  return section.violationId || section.xmlId || section.sectionCode || section.ordinal || section.label || `section-${index}`;
}

function sectionParentId(section: ViolationChapterSectionEntry): string | undefined {
  return section.parentViolationId || section.parentXmlId;
}

function buildSectionLabel(section: ViolationChapterSectionEntry, violation?: ViolationIndexEntry): string {
  const code = humanizeLabel(section.sectionCode || section.ordinal || violation?.code);
  const title = humanizeLabel(section.title || section.label || violation?.title);
  const fallback = humanizeLabel(section.displayText || section.description || section.contentText || violation?.displayText || violation?.contentSnippet);
  return joinCodeAndTitle(code, title) || fallback || code || title || 'Untitled section';
}

function sumSelectableNodes(nodes: BrowseNode[]): number {
  return nodes.reduce((total, node) => total + (node.violation ? 1 : 0) + sumSelectableNodes(node.children), 0);
}

function buildSectionTree(sections: ViolationChapterSectionEntry[], violations: ViolationIndexEntry[]): BrowseNode[] {
  const byRawNodeId = new Map(violations.filter((v) => v.rawNodeId).map((v) => [v.rawNodeId as string, v]));
  const byId = new Map(violations.map((v) => [v._id, v]));
  const byParentSourceId = new Map(violations.filter((v) => v.rawNodeId).map((v) => [v.rawNodeId as string, v]));
  const nodes = new Map<string, BrowseNode & { parentKey?: string; sourceIndex: number }>();
  sections.forEach((section, index) => {
    const key = sectionNodeId(section, index);
    const violation = (section.violationId ? byId.get(section.violationId) || byRawNodeId.get(section.violationId) : undefined) || byRawNodeId.get(key) || byParentSourceId.get(key);
    nodes.set(key, {
      key,
      label: buildSectionLabel(section, violation),
      violation,
      children: [],
      count: 0,
      parentKey: sectionParentId(section),
      sourceIndex: index,
    });
  });
  const roots: Array<BrowseNode & { sourceIndex: number }> = [];
  for (const node of nodes.values()) {
    const parent = node.parentKey ? nodes.get(node.parentKey) : undefined;
    if (parent) parent.children.push(node);
    else roots.push(node);
  }
  const clean = (items: Array<BrowseNode & { sourceIndex?: number }>): BrowseNode[] => items
    .sort((a, b) => (a.sourceIndex ?? 0) - (b.sourceIndex ?? 0))
    .map((node) => {
      const children = clean(node.children as Array<BrowseNode & { sourceIndex?: number }>);
      return { key: node.key, label: node.label, violation: node.violation, children, count: (node.violation ? 1 : 0) + sumSelectableNodes(children) };
    });
  return clean(roots);
}

function buildBrowseTree(chapters: ViolationChapterEntry[], violations: ViolationIndexEntry[], codeSetById: Map<string, ViolationCodeSetEntry>): BrowseNode[] {
  if (chapters.length === 0) return [];
  return [...chapters]
    .sort((a, b) => (a.ordinal || '').localeCompare(b.ordinal || '', undefined, { numeric: true }))
    .map((chapter) => {
      const codeSetName = codeSetById.get(chapter.codeSetId)?.name;
      const chapterViolations = violations.filter((v) => v.codeSetId === chapter.codeSetId && (v.rawRootId === chapter.chapterId || v.chapterTitle === chapter.title || v.chapterOrdinal === chapter.ordinal));
      const children = buildSectionTree(chapter.sections ?? [], chapterViolations);
      const label = codeSetName ? `${codeSetName} · ${chapterLabel(chapter)}` : chapterLabel(chapter);
      return { key: `chapter:${chapter._id}`, label, children, count: sumSelectableNodes(children) };
    });
}
// ---------------------------------------------------------------------------
// Internal component
// ---------------------------------------------------------------------------

/**
 * @component ViolationPicker
 *
 * Purpose:
 * - Reusable code-library picker. 2-step UX: pick a code set (or "All" when
 *   `codeSetIds` is supplied externally), then search/select violations.
 *
 * Props:
 * - See `ViolationPickerProps`.
 *
 * Behavior:
 * - Subscribes to Dexie liveQuery for code sets + violations + counts.
 *   On Dexie change (e.g. sync delivers new entries), the list re-renders
 *   automatically.
 *
 * Data Source:
 * - local — Dexie `violationCodeSets` and `violationIndex`.
 *
 * Constraints:
 * - No API calls. No mock data. Honors local-first contract.
 *
 * Performance:
 * - Ranker runs on the client. With ~10k entries the call is sub-10ms.
 *   When that becomes a concern we plug in `rankFn` for AI/server ranker.
 */
export function ViolationPicker({
  db,
  mode = 'single',
  selectedIds = [],
  onChange,
  onPickOne,
  codeSetIds,
  rankFn,
  asyncRankFn,
  aiSearchDefaultEnabled = true,
  aiToggleStorageKey = 'firesquirrel.violationPicker.aiSearchEnabled',
  placeholder = 'Search violations…',
  autoFocus = false,
  emptyHint,
  className,
}: ViolationPickerProps) {
  const externalScopeFixed = !!(codeSetIds && codeSetIds.length > 0);
  const [activeCodeSetId, setActiveCodeSetId] = useState<string | null>(
    externalScopeFixed && codeSetIds!.length === 1 ? codeSetIds![0] : null,
  );
  const [allCodeSetsSelected, setAllCodeSetsSelected] = useState(false);
  const [activeTab, setActiveTab] = useState<'browse' | 'text' | 'ai'>('browse');
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [query, setQuery] = useState('');

  // Subscribe to active code sets.
  const [codeSets, setCodeSets] = useState<ViolationCodeSetEntry[]>([]);
  useEffect(() => {
    if (!db) return undefined;
    const sub = watchActiveViolationCodeSets(db).subscribe({
      next: (rows) => {
        // Apply external `codeSetIds` restriction so the chooser shows only
        // sets the host wants exposed.
        if (codeSetIds && codeSetIds.length > 0) {
          const allowed = new Set(codeSetIds);
          setCodeSets(rows.filter((cs) => allowed.has(cs._id)));
        } else {
          setCodeSets(rows);
        }
      },
      error: (err) => console.error('[ViolationPicker] code-sets liveQuery error:', err),
    });
    return () => sub.unsubscribe();
  }, [db, codeSetIds?.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  // Subscribe to per-set counts (only when picking a code set).
  const [counts, setCounts] = useState<Record<string, number>>({});
  useEffect(() => {
    if (!db) return undefined;
    const sub = watchViolationCountsByCodeSet(db).subscribe({
      next: setCounts,
      error: (err) => console.error('[ViolationPicker] counts liveQuery error:', err),
    });
    return () => sub.unsubscribe();
  }, [db]);

  // Step A.11 gap-closure (2026-05-01): surface a banner inside the picker
  // when the currently-active code set is still downloading from the vendor.
  // We resolve "hydrating" by watching active `codeSetDownload` background
  // jobs and matching `payload.bookId` against the code set's
  // `iccMetadata.bookId` / `nfpaMetadata.standardNumber`.
  const [hydratingBookIds, setHydratingBookIds] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (!db) return undefined;
    const sub = watchActiveBackgroundJobsByType(db, 'codeSetDownload').subscribe({
      next: (jobs: BackgroundJob[]) => {
        const next = new Set<string>();
        for (const job of jobs) {
          const payload = (job.payload ?? {}) as Record<string, unknown>;
          const bookId = typeof payload.bookId === 'string' ? payload.bookId : '';
          if (bookId) next.add(bookId);
        }
        setHydratingBookIds(next);
      },
      error: (err) => console.warn('[ViolationPicker] hydrating jobs liveQuery error:', err),
    });
    return () => sub.unsubscribe();
  }, [db]);

  function codeSetBookKey(cs: ViolationCodeSetEntry | undefined): string | undefined {
    if (!cs) return undefined;
    const icc = (cs as any).iccMetadata?.bookId;
    const nfpa = (cs as any).nfpaMetadata?.standardNumber;
    return icc || nfpa || undefined;
  }

  function isCodeSetHydrating(cs: ViolationCodeSetEntry | undefined): boolean {
    const key = codeSetBookKey(cs);
    return !!key && hydratingBookIds.has(key);
  }

  // Subscribe to the violation index for the active scope.
  const scopeIds = useMemo<string[] | undefined>(() => {
    if (activeCodeSetId) return [activeCodeSetId];
    if (codeSetIds && codeSetIds.length > 0) return codeSetIds;
    if (allCodeSetsSelected) return undefined;
    return undefined;
  }, [activeCodeSetId, allCodeSetsSelected, codeSetIds?.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  const [violations, setViolations] = useState<ViolationIndexEntry[]>([]);
  useEffect(() => {
    if (!db) return undefined;
    const sub = watchViolationIndex(db, scopeIds).subscribe({
      next: setViolations,
      error: (err) => console.error('[ViolationPicker] violations liveQuery error:', err),
    });
    return () => sub.unsubscribe();
  }, [db, scopeIds?.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  const [chapters, setChapters] = useState<ViolationChapterEntry[]>([]);
  useEffect(() => {
    if (!db) return undefined;
    const sub = watchViolationChapters(db, scopeIds).subscribe({
      next: setChapters,
      error: (err) => console.error('[ViolationPicker] chapters liveQuery error:', err),
    });
    return () => sub.unsubscribe();
  }, [db, scopeIds?.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  // -----------------------------------------------------------------------
  // AI smart-search wiring
  // -----------------------------------------------------------------------
  // Persist the AI toggle so an inspector's preference sticks across sessions.
  const [aiEnabled, setAiEnabled] = useState<boolean>(() => {
    if (typeof window === 'undefined') return aiSearchDefaultEnabled;
    try {
      const stored = window.localStorage.getItem(aiToggleStorageKey);
      if (stored === 'true') return true;
      if (stored === 'false') return false;
      return aiSearchDefaultEnabled;
    } catch {
      return aiSearchDefaultEnabled;
    }
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(aiToggleStorageKey, String(aiEnabled));
    } catch {
      /* ignore quota errors */
    }
  }, [aiEnabled, aiToggleStorageKey]);

  // Debounced query for the AI ranker — avoids hammering the API on every
  // keystroke. Lexical ranking still runs on every keystroke from the
  // synchronous `query` so the UI stays snappy.
  const [debouncedQuery, setDebouncedQuery] = useState(query);
  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedQuery(query), 250);
    return () => window.clearTimeout(handle);
  }, [query]);

  // Lexical ranking is always computed (used as fallback + while AI loads).
  const lexicalRanked = useMemo(() => {
    const ranker = rankFn ?? rankViolationEntries;
    return ranker(violations, query, { limit: 50, codeSetIds: scopeIds });
  }, [violations, query, rankFn, scopeIds?.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  const [aiRanked, setAiRanked] = useState<ViolationIndexEntry[] | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const aiAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!asyncRankFn || !aiEnabled) {
      setAiRanked(null);
      setAiLoading(false);
      setAiError(null);
      return;
    }
    // No query → no semantic ranking; use lexical (which falls through to
    // alphabetical when query is empty).
    if (!debouncedQuery.trim()) {
      setAiRanked(null);
      setAiLoading(false);
      setAiError(null);
      return;
    }
    if (violations.length === 0) {
      setAiRanked([]);
      setAiLoading(false);
      return;
    }

    // Cancel any in-flight call before starting a new one.
    aiAbortRef.current?.abort();
    const controller = new AbortController();
    aiAbortRef.current = controller;

    let cancelled = false;
    setAiLoading(true);
    setAiError(null);
    asyncRankFn(violations, debouncedQuery, {
      limit: 50,
      codeSetIds: scopeIds,
      signal: controller.signal,
    })
      .then((next) => {
        if (cancelled || controller.signal.aborted) return;
        setAiRanked(next);
      })
      .catch((err: unknown) => {
        if (cancelled || controller.signal.aborted) return;
        const message = err instanceof Error ? err.message : String(err);
        // Eat AbortError: caused by our own cancel paths.
        if (/abort/i.test(message)) return;
        console.warn('[ViolationPicker] AI ranker failed; falling back to lexical:', err);
        setAiError(message);
        setAiRanked(null);
      })
      .finally(() => {
        if (!cancelled && !controller.signal.aborted) setAiLoading(false);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [
    asyncRankFn,
    aiEnabled,
    debouncedQuery,
    violations,
    scopeIds?.join(','), // eslint-disable-line react-hooks/exhaustive-deps
  ]);

  const textRanked = lexicalRanked;
  const aiRankedResults = aiRanked ?? lexicalRanked;
  const showAiTab = !!asyncRankFn;

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const codeSetById = useMemo(() => new Map(codeSets.map((cs) => [cs._id, cs])), [codeSets]);
  const groupedViolations = useMemo(() => {
    const source = activeTab === 'ai' ? aiRankedResults : textRanked;
    const groups = new Map<string, { key: string; title: string; rows: ViolationIndexEntry[] }>();
    for (const row of source) {
      const codeSetName = codeSetById.get(row.codeSetId)?.name ?? row.codeSetId;
      const chapter = humanizeLabel(row.chapterTitle || row.category) || 'Uncategorized';
      const chapterOrdinal = row.chapterOrdinal ? `Chapter ${row.chapterOrdinal}` : 'Chapter';
      const key = `${row.codeSetId}::${chapterOrdinal}::${chapter}`;
      const title = allCodeSetsSelected || externalScopeFixed && (codeSetIds?.length ?? 0) > 1
        ? `${codeSetName} · ${chapterOrdinal}: ${chapter}`
        : `${chapterOrdinal}: ${chapter}`;
      if (!groups.has(key)) groups.set(key, { key, title, rows: [] });
      groups.get(key)!.rows.push(row);
    }
    return Array.from(groups.values()).map((group) => ({
      ...group,
      rows: group.rows.sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true })),
    }));
  }, [activeTab, textRanked, aiRankedResults, codeSetById, allCodeSetsSelected, externalScopeFixed, codeSetIds?.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  const browseTree = useMemo(() => buildBrowseTree(chapters, violations, codeSetById), [chapters, violations, codeSetById]);

  function emitMultiToggle(entry: ViolationIndexEntry) {
    if (!onChange) return;
    const exists = selectedSet.has(entry._id);
    const next: ViolationPickerSelection[] = exists
      ? // Filter out
        selectedIds
          .filter((id) => id !== entry._id)
          .map((id) => {
            const found = violations.find((v) => v._id === id);
            return found
              ? {
                  violationId: found._id,
                  codeSetId: found.codeSetId,
                  code: found.code,
                  title: found.title,
                  codeReference: found.codeReference,
                  contentSnippet: found.contentSnippet,
                  displayText: found.displayText,
                  bodyText: found.bodyText,
                }
              : { violationId: id, codeSetId: '', code: '', title: '' };
          })
      : // Append
        [
          ...selectedIds.map((id) => {
            const found = violations.find((v) => v._id === id);
            return found
              ? {
                  violationId: found._id,
                  codeSetId: found.codeSetId,
                  code: found.code,
                  title: found.title,
                  codeReference: found.codeReference,
                  contentSnippet: found.contentSnippet,
                  displayText: found.displayText,
                  bodyText: found.bodyText,
                }
              : { violationId: id, codeSetId: '', code: '', title: '' };
          }),
          {
            violationId: entry._id,
            codeSetId: entry.codeSetId,
            code: entry.code,
            title: entry.title,
            codeReference: entry.codeReference,
            contentSnippet: entry.contentSnippet,
            displayText: entry.displayText,
            bodyText: entry.bodyText,
          },
        ];
    onChange(next);
  }

  function emitSinglePick(entry: ViolationIndexEntry) {
    onPickOne?.({
      violationId: entry._id,
      codeSetId: entry.codeSetId,
      code: entry.code,
      title: entry.title,
      codeReference: entry.codeReference,
      contentSnippet: entry.contentSnippet,
      displayText: entry.displayText,
      bodyText: entry.bodyText,
    });
  }

  function renderViolationRow(v: ViolationIndexEntry) {
    const checked = mode === 'multi' && selectedSet.has(v._id);
    const codeSetName = codeSetById.get(v.codeSetId)?.name ?? v.codeSetId;
    return (
      <button
        key={v._id}
        type="button"
        onClick={() => mode === 'multi' ? emitMultiToggle(v) : emitSinglePick(v)}
        className={`grid w-full grid-cols-[auto_1fr_auto] items-start gap-3 px-3 py-2.5 text-left transition-colors ${
          checked ? 'bg-orange-50 hover:bg-orange-100' : 'hover:bg-orange-50'
        }`}
        data-testid={`violation-picker-row-${v._id}`}
      >
        <span className="rounded-sm bg-slate-100 px-2 py-1 text-[11px] font-mono font-bold text-slate-700">
          {v.code}
        </span>
        <span className="min-w-0">
          <span className="block text-xs font-semibold text-gray-900">{v.title}</span>
          <span className="mt-0.5 block text-[11px] text-gray-500 line-clamp-2">
            {v.codeReference ? `${v.codeReference} · ` : ''}{v.contentSnippet || v.displayText || v.bodyText || 'No violation text preview available.'}
          </span>
          {(allCodeSetsSelected || externalScopeFixed && (codeSetIds?.length ?? 0) > 1) && (
            <span className="mt-1 inline-flex rounded-sm bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700">{codeSetName}</span>
          )}
        </span>
        <span className="inline-flex items-center gap-1 rounded-sm bg-orange-600 px-2 py-1 text-[10px] font-semibold text-white">
          {mode === 'multi' && checked ? <Check className="h-3 w-3" /> : null}
          Select
        </span>
      </button>
    );
  }

  function renderBrowseNode(node: BrowseNode, depth = 0): React.ReactNode {
    const expanded = expandedGroups[node.key] ?? depth < 2;
    const hasChildren = node.children.length > 0;
    const paddingLeft = 12 + depth * 18;
    if (node.violation && !hasChildren) {
      return <div key={node.key} style={{ paddingLeft }}>{renderViolationRow(node.violation)}</div>;
    }
    return (
      <div key={node.key} className="border-b border-gray-100 last:border-b-0">
        <button
          type="button"
          onClick={() => hasChildren ? toggleGroup(node.key) : node.violation ? (mode === 'multi' ? emitMultiToggle(node.violation) : emitSinglePick(node.violation)) : undefined}
          className="flex w-full items-center justify-between bg-slate-50 py-2 pr-3 text-left text-xs font-semibold text-slate-800 hover:bg-slate-100"
          style={{ paddingLeft }}
        >
          <span className="flex min-w-0 items-center gap-2">
            {hasChildren ? <ChevronDown className={`h-3.5 w-3.5 shrink-0 transition-transform ${expanded ? '' : '-rotate-90'}`} /> : <span className="h-3.5 w-3.5 shrink-0" />}
            <span className="truncate">{node.label}</span>
          </span>
          <span className="shrink-0 text-[10px] font-medium text-slate-500">{node.count} code{node.count === 1 ? '' : 's'}</span>
        </button>
        {expanded ? <div>{node.children.map((child) => renderBrowseNode(child, depth + 1))}</div> : null}
      </div>
    );
  }
  function toggleGroup(key: string) {
    setExpandedGroups((prev) => ({ ...prev, [key]: !(prev[key] ?? true) }));
  }

  const showCodeSetChooser = !externalScopeFixed && !activeCodeSetId && !allCodeSetsSelected;

  // -----------------------------------------------------------------------
  // Step 1 — pick a code set
  // -----------------------------------------------------------------------
  if (showCodeSetChooser) {
    if (codeSets.length === 0) {
      return (
        <div className={className} data-testid="violation-picker">
          <p className="text-xs text-gray-400 italic py-4 text-center">
            {emptyHint ?? 'No active code sets available. Sync to download code sets.'}
          </p>
        </div>
      );
    }
    return (
      <div className={className} data-testid="violation-picker">
        <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
          <p className="text-sm font-semibold text-slate-900">Choose the code set to search</p>
          <p className="mt-1 text-xs text-slate-600">Start with the adopted book for this department when possible. You can still search every active code set if you need a broader lookup.</p>
        </div>
        <div className="mb-3">
          <button
            type="button"
            onClick={() => setAllCodeSetsSelected(true)}
            className="flex w-full items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-left transition-colors hover:bg-amber-100"
            data-testid="violation-picker-all-codesets"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-amber-900">Search all code sets</span>
              <span className="mt-0.5 block text-xs text-amber-800">
                Broad search can include older or alternate books and may take longer. A specific code set usually returns the most current violation text.
              </span>
            </span>
            <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-amber-600" />
          </button>
        </div>
        <p className="mb-2 text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Available code sets</p>
        <div className="grid gap-2 md:grid-cols-2">
          {codeSets.map((cs) => {
            const hydrating = isCodeSetHydrating(cs);
            return (
              <button
                key={cs._id}
                type="button"
                onClick={() => setActiveCodeSetId(cs._id)}
                className="flex w-full items-center gap-2.5 rounded-xl border border-gray-200 bg-white px-3 py-3 text-left shadow-xs transition-colors hover:border-orange-200 hover:bg-orange-50"
                data-testid={`violation-picker-codeset-${cs._id}`}
              >
                <BookOpen className="h-3.5 w-3.5 text-orange-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-gray-900 truncate">{cs.name}</div>
                  {cs.description && (
                    <div className="text-[10px] text-gray-500 truncate">{cs.description}</div>
                  )}
                </div>
                {hydrating ? (
                  <span
                    className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] font-medium bg-orange-50 text-orange-700 border border-orange-200"
                    title="This code set is still downloading from the vendor."
                    data-testid={`violation-picker-hydrating-${cs._id}`}
                  >
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Hydrating
                  </span>
                ) : (
                  <span className="shrink-0 text-[10px] text-gray-400">
                    {counts[cs._id] ?? 0} codes
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // Step 2 — search + pick a violation
  // -----------------------------------------------------------------------
  const activeCs = codeSets.find((cs) => cs._id === activeCodeSetId);

  return (
    <div className={className} data-testid="violation-picker">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-900">Find and select a violation</p>
          <p className="text-xs text-slate-500">
            {allCodeSetsSelected ? 'Searching all active code sets' : activeCs?.name ?? 'Selected code sets'}
          </p>
        </div>
        {!externalScopeFixed && (
          <button
            type="button"
            onClick={() => { setActiveCodeSetId(null); setAllCodeSetsSelected(false); setQuery(''); }}
            className="inline-flex items-center gap-1 rounded-lg border border-orange-200 bg-orange-50 px-2.5 py-1.5 text-xs font-medium text-orange-700 hover:bg-orange-100"
            data-testid="violation-picker-back"
          >
            <ChevronLeft className="h-3 w-3" /> Change code set
          </button>
        )}
      </div>

      {allCodeSetsSelected ? (
        <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <strong>All code sets selected.</strong> Results may include older or alternate books and can take longer to search. Use a specific adopted code set when possible for the most current violation text.
        </div>
      ) : null}

      {/* Step A.11 hydration banner — full-width notice when the active code
          set is still downloading from the vendor. */}
      {isCodeSetHydrating(activeCs) ? (
        <div
          className="mb-2 rounded-md border border-orange-200 bg-orange-50 px-2.5 py-2 text-[11px] text-orange-800 flex items-start gap-2"
          data-testid="violation-picker-hydration-banner"
        >
          <Loader2 className="h-3.5 w-3.5 text-orange-500 shrink-0 animate-spin mt-0.5" />
          <div>
            <strong>This code set is still downloading.</strong> Pick it now — the
            violation list appears automatically once the download completes.
            Widgets saved before hydration finishes are marked “Violation to be
            added later” and surface in Bind Violations.
          </div>
        </div>
      ) : null}

      <div className="mb-3 grid grid-cols-3 rounded-xl border border-slate-200 bg-slate-50 p-1">
        {[
          { id: 'browse' as const, label: 'Full List', icon: ListTree },
          { id: 'text' as const, label: 'Standard Search', icon: Search },
          { id: 'ai' as const, label: 'AI Smart Search', icon: Sparkles, disabled: !showAiTab },
        ].map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              type="button"
              disabled={tab.disabled}
              onClick={() => {
                setActiveTab(tab.id);
                if (tab.id === 'ai') setAiEnabled(true);
              }}
              className={`inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold transition-colors ${
                activeTab === tab.id
                  ? 'bg-white text-orange-700 shadow-xs'
                  : tab.disabled
                    ? 'text-slate-300'
                    : 'text-slate-600 hover:bg-white/70'
              }`}
              data-testid={`violation-picker-tab-${tab.id}`}
            >
              <Icon className="h-3.5 w-3.5" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {(activeTab === 'text' || activeTab === 'ai') && (
      <div className="flex items-center gap-1.5 mb-3">
        <div className="relative flex-1">
          <Search className="h-3.5 w-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={placeholder}
            autoFocus={autoFocus}
            className="w-full pl-8 pr-7 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-orange-400"
            data-testid="violation-picker-search"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              aria-label="Clear search"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>
      )}

      {activeTab === 'ai' && aiError && (
        <p
          className="mb-2 px-2 py-1 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md"
          data-testid="violation-picker-ai-error"
        >
          AI search unavailable — falling back to text search.
        </p>
      )}

      {/* Multi-mode selection summary */}
      {mode === 'multi' && selectedIds.length > 0 && (
        <div className="mb-2 px-2 py-1 text-[10px] text-orange-700 bg-orange-50 border border-orange-200 rounded-md">
          {selectedIds.length} violation{selectedIds.length === 1 ? '' : 's'} selected
        </div>
      )}

      {activeTab === 'ai' && aiLoading ? (
        <div className="mb-2 inline-flex items-center gap-2 rounded-lg bg-orange-50 px-3 py-2 text-xs font-medium text-orange-700">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Ranking local violations with AI…
        </div>
      ) : null}

      <div className="max-h-[52vh] overflow-y-auto rounded-xl border border-gray-100 bg-white">
        {activeTab === 'browse' ? (
          browseTree.length === 0 ? (
            <p className="px-3 py-3 text-xs text-gray-400 italic text-center">
              No chapter hierarchy is available in this scope yet.
            </p>
          ) : (
            browseTree.map((node) => renderBrowseNode(node))
          )
        ) : groupedViolations.length === 0 ? (
          <p className="px-3 py-3 text-xs text-gray-400 italic text-center">
            {query ? 'No violations match your search.' : 'No violations in this scope.'}
          </p>
        ) : (
          groupedViolations.map((group) => {
            const expanded = expandedGroups[group.key] ?? true;
            return (
              <div key={group.key} className="border-b border-gray-100 last:border-b-0">
                <button
                  type="button"
                  onClick={() => toggleGroup(group.key)}
                  className="flex w-full items-center justify-between bg-slate-50 px-3 py-2 text-left text-xs font-semibold text-slate-700 hover:bg-slate-100"
                >
                  <span>{group.title}</span>
                  <span className="inline-flex items-center gap-2 text-[10px] text-slate-500">
                    {group.rows.length} rows
                    <ChevronDown className={`h-3 w-3 transition-transform ${expanded ? '' : '-rotate-90'}`} />
                  </span>
                </button>
                {expanded ? <div className="divide-y divide-gray-100">{group.rows.map(renderViolationRow)}</div> : null}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export default ViolationPicker;
